import React, { useState, useCallback } from 'react';
import { MapControl, ControlPosition, AdvancedMarker, InfoWindow, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useDrawingManager } from './EstateDrawingManager';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const isValidCoordinate = (lat, lng) => {
    const validLat = typeof lat === 'number' && !isNaN(lat) && lat >= -90 && lat <= 90;
    const validLng = typeof lng === 'number' && !isNaN(lng) && lng >= -180 && lng <= 180;
    return validLat && validLng;
};

const scrollToResults = () => {
    const resultsElement = document.getElementById('estates-results');
    if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
    }
};

const MapContent = ({ estates, selectedEstate, onMarkerClick, onPolygonFilter, onClearPolygon }) => {
    const { t } = useTranslation('common');
    const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [estatesInPolygon, setEstatesInPolygon] = useState([]);

    const geometry = useMapsLibrary('geometry');
    const core = useMapsLibrary('core');

    const handlePolygonComplete = useCallback((polygon) => {
        if (!geometry || !core) return;

        const filtered = estates.filter(estate => {
            const lat = parseFloat(estate.latitude);
            const lng = parseFloat(estate.longitude);

            if (!isValidCoordinate(lat, lng)) return false;

            const point = new core.LatLng(lat, lng);
            return geometry.poly.containsLocation(point, polygon);
        });

        setEstatesInPolygon(filtered);
        onPolygonFilter(filtered);
        setIsDrawingMode(false);
    }, [estates, geometry, core, onPolygonFilter]);

    const { startDrawing, clearOverlay, activeOverlay, isReady } = useDrawingManager(handlePolygonComplete);

    const handleMarkerClick = useCallback((estate) => {
        onMarkerClick(estate);
        setIsInfoWindowOpen(true);
    }, [onMarkerClick]);

    const handleInfoWindowClose = useCallback(() => {
        setIsInfoWindowOpen(false);
    }, []);

    const clearPolygon = useCallback(() => {
        clearOverlay();
        setEstatesInPolygon([]);
        onClearPolygon();
    }, [clearOverlay, onClearPolygon]);

    const handleStartDrawing = useCallback(() => {
        if (isReady) {
            startDrawing();
            setIsDrawingMode(true);
        }
    }, [isReady, startDrawing]);

    return (
        <>
            {estates?.map(estate => {
                const lat = parseFloat(estate.latitude);
                const lng = parseFloat(estate.longitude);

                if (isValidCoordinate(lat, lng)) {
                    return (
                        <AdvancedMarker
                            key={estate.id}
                            position={{ lat, lng }}
                            onClick={() => handleMarkerClick(estate)}
                        />
                    );
                }
                return null;
            })}

            {selectedEstate && isInfoWindowOpen && (
                <InfoWindow
                    position={{
                        lat: parseFloat(selectedEstate.latitude),
                        lng: parseFloat(selectedEstate.longitude)
                    }}
                    onCloseClick={handleInfoWindowClose}
                >
                    <div className="max-w-xs">
                        <img
                            src={selectedEstate.image}
                            alt={selectedEstate.title}
                            className="w-full h-32 object-cover mb-2 rounded"
                        />
                        <h3 className="font-bold text-lg mb-1">{selectedEstate.title}</h3>
                        <p className="text-gray-600 mb-1">{selectedEstate.location}</p>
                        <p className="text-green-600 font-bold mb-2">€{selectedEstate.price}</p>
                        <div className="flex justify-between text-sm">
                            <span>{selectedEstate.bedrooms} {t('map.rooms')}</span>
                            <span>{selectedEstate.bathrooms} {t('map.bathrooms')}</span>
                            <span>{selectedEstate.squareFeet} {t('map.squareMeters')}</span>
                        </div>
                        <Link
                            to={`/estate/${selectedEstate.id}`}
                            className="block mt-3 text-center bg-sky-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
                        >
                            Se detaljer
                        </Link>
                    </div>
                </InfoWindow>
            )}

            <MapControl position={ControlPosition.BOTTOM_CENTER}>
                <div className="mb-8">
                    {isDrawingMode && !activeOverlay && (
                        <div className="bg-white/90 text-gray-800 font-semibold text-sm py-2 px-4 rounded-full shadow-lg">
                            {t('map.drawHint', 'Click and drag on the map to draw an area')}
                        </div>
                    )}

                    {!isDrawingMode && !activeOverlay && (
                        <button
                            onClick={handleStartDrawing}
                            className="bg-sky-600 font-semibold text-sm text-white py-2 px-4 rounded-full hover:bg-sky-700 transition-colors shadow-lg"
                        >
                            {t('map.drawArea')}
                        </button>
                    )}

                    {activeOverlay && (
                        <div className="flex gap-2">
                            <button
                                onClick={clearPolygon}
                                className="bg-red-600 font-semibold text-sm text-white py-2 px-4 rounded-full hover:bg-red-700 transition-colors shadow-lg"
                            >
                                {t('map.clearArea')}
                            </button>
                            <button
                                onClick={scrollToResults}
                                className="bg-green-600 font-semibold text-sm text-white py-2 px-4 rounded-full hover:bg-green-700 transition-colors shadow-lg"
                            >
                                {t('map.viewProperties', { count: estatesInPolygon.length })}
                            </button>
                        </div>
                    )}
                </div>
            </MapControl>

            <MapControl position={ControlPosition.TOP_CENTER}>
            </MapControl>
        </>
    );
};

export default MapContent;